import { add } from '../Context/dist/effect-Context.esm.js';
import { struct } from '../Data/dist/effect-Data.esm.js';
import { decode, toMillis } from '../Duration/dist/effect-Duration.esm.js';
import { equals } from '../Equal/dist/effect-Equal.esm.js';
import { succeed as succeed$1, matchEffect, unit as unit$1 } from '../Exit/dist/effect-Exit.esm.js';
import { pipe } from '../Function/dist/effect-Function.esm.js';
import { fromIterable } from '../HashSet/dist/effect-HashSet.esm.js';
import { makeCacheStats, makeEntryStats, makeMapKey, makeKeySet } from './cache.esm.js';
import { flatMap, context, sync, suspend, flatten, zipRight, map, succeed, unit, asUnit, as, onInterrupt, provideContext, exit, failCause } from './core.esm.js';
import { acquireRelease, forEachParUnboundedDiscard, scopedEffect } from './fiberRuntime.esm.js';
import { size, get, has, set, remove, empty } from '../MutableHashMap/dist/effect-MutableHashMap.esm.js';
import { offer, poll, EmptyMutableQueue, unbounded } from '../MutableQueue/dist/effect-MutableQueue.esm.js';
import { make as make$1, compareAndSet, set as set$1, incrementAndGet, decrementAndGet } from '../MutableRef/dist/effect-MutableRef.esm.js';
import { match, getOrUndefined, none, some } from '../Option/dist/effect-Option.esm.js';
import { pipeArguments } from '../Pipeable/dist/effect-Pipeable.esm.js';
import { make as make$2, Scope, close } from '../Scope/dist/effect-Scope.esm.js';
import { clock, succeedNone, memoize, asSome, when } from './core-effect.esm.js';

/**
 * The `CacheState` represents the mutable state underlying the cache.
 *
 * @internal
 */

/** @internal */
const makeCacheState = (map, keys, accesses, updating, hits, misses) => ({
  map,
  keys,
  accesses,
  updating,
  hits,
  misses
});

/**
 * Constructs an initial cache state.
 *
 * @internal
 */
const initialCacheState = () => makeCacheState(empty(), makeKeySet(), unbounded(), make$1(false), 0, 0);

/**
 * A `MapValue` represents a value in the cache. A value may either be
 * `Pending` with a `Promise` that will contain the result of computing the
 * lookup function, when it is available, or `Complete` with an `Exit` value
 * that contains the result of computing the lookup function.
 *
 * @internal
 */

/** @internal */

/** @internal */

/** @internal */

/** @internal */
const complete = (key, exit, ownerCount, entryStats, timeToLive) => struct({
  _tag: "Complete",
  key,
  exit,
  ownerCount,
  entryStats,
  timeToLive
});

/** @internal */
const pending = (key, scoped) => struct({
  _tag: "Pending",
  key,
  scoped
});

/** @internal */
const refreshing = (scoped, complete) => struct({
  _tag: "Refreshing",
  scoped,
  complete
});

/** @internal */
const toScoped = self => matchEffect(self.exit, {
  onFailure: cause => failCause(cause),
  onSuccess: ([value]) => acquireRelease(as(sync(() => incrementAndGet(self.ownerCount)), value), () => releaseOwner(self))
});

/** @internal */
const releaseOwner = self => matchEffect(self.exit, {
  onFailure: () => unit,
  onSuccess: ([, finalizer]) => flatMap(sync(() => decrementAndGet(self.ownerCount)), numOwner => when(finalizer(unit$1), () => numOwner === 0))
});

/** @internal */
const ScopedCacheSymbolKey = "effect/ScopedCache";

/** @internal */
const ScopedCacheTypeId = /*#__PURE__*/Symbol.for(ScopedCacheSymbolKey);
const scopedCacheVariance = {
  _Key: _ => _,
  _Error: _ => _,
  _Value: _ => _
};
class ScopedCacheImpl {
  [ScopedCacheTypeId] = scopedCacheVariance;
  constructor(capacity, scopedLookup, clock, timeToLive, context) {
    this.capacity = capacity;
    this.scopedLookup = scopedLookup;
    this.clock = clock;
    this.timeToLive = timeToLive;
    this.context = context;
    this.cacheState = initialCacheState();
  }
  pipe() {
    return pipeArguments(this, arguments);
  }
  cacheStats() {
    return sync(() => makeCacheStats({
      hits: this.cacheState.hits,
      misses: this.cacheState.misses,
      size: size(this.cacheState.map)
    }));
  }
  getOption(key) {
    return suspend(() => match(get(this.cacheState.map, key), {
      onNone: () => succeedNone,
      onSome: value => flatten(this.resolveMapValue(value))
    }));
  }
  getOptionComplete(key) {
    return suspend(() => match(get(this.cacheState.map, key), {
      onNone: () => succeedNone,
      onSome: value => flatten(this.resolveMapValue(value, true))
    }));
  }
  contains(key) {
    return sync(() => has(this.cacheState.map, key));
  }
  entryStats(key) {
    return sync(() => {
      const value = getOrUndefined(get(this.cacheState.map, key));
      if (value === undefined) {
        return none();
      }
      switch (value._tag) {
        case "Complete":
          {
            return some(makeEntryStats(value.entryStats.loadedMillis));
          }
        case "Pending":
          {
            return none();
          }
        case "Refreshing":
          {
            return some(makeEntryStats(value.complete.entryStats.loadedMillis));
          }
      }
    });
  }
  get(key) {
    return pipe(this.lookupValueOf(key), memoize, flatMap(lookupValue => suspend(() => {
      let k = undefined;
      let value = getOrUndefined(get(this.cacheState.map, key));
      if (value === undefined) {
        k = makeMapKey(key);
        if (has(this.cacheState.map, key)) {
          value = getOrUndefined(get(this.cacheState.map, key));
        } else {
          set(this.cacheState.map, key, pending(k, lookupValue));
        }
      }
      if (value === undefined) {
        this.trackMiss();
        return zipRight(this.ensureMapSizeNotExceeded(k), lookupValue);
      }
      return map(this.resolveMapValue(value), flatMap(match({
        onNone: () => {
          const val = value;
          const current = getOrUndefined(get(this.cacheState.map, key));
          if (equals(current, value)) {
            remove(this.cacheState.map, key);
          }
          return pipe(this.ensureMapSizeNotExceeded(val.key), zipRight(releaseOwner(val)), zipRight(this.get(key)));
        },
        onSome: succeed
      })));
    })), flatten);
  }
  invalidate(key) {
    return suspend(() => {
      if (has(this.cacheState.map, key)) {
        const mapValue = getOrUndefined(get(this.cacheState.map, key));
        remove(this.cacheState.map, key);
        switch (mapValue._tag) {
          case "Complete":
            {
              return releaseOwner(mapValue);
            }
          case "Pending":
            {
              return unit;
            }
          case "Refreshing":
            {
              return releaseOwner(mapValue.complete);
            }
        }
      }
      return unit;
    });
  }
  invalidateAll() {
    return forEachParUnboundedDiscard(fromIterable(Array.from(this.cacheState.map).map(([key]) => key)), key => this.invalidate(key), false);
  }
  refresh(key) {
    return pipe(this.lookupValueOf(key), memoize, flatMap(scoped => {
      let value = getOrUndefined(get(this.cacheState.map, key));
      let newKey = undefined;
      if (value === undefined) {
        newKey = makeMapKey(key);
        if (has(this.cacheState.map, key)) {
          value = getOrUndefined(get(this.cacheState.map, key));
        } else {
          set(this.cacheState.map, key, pending(newKey, scoped));
        }
      }
      let finalScoped;
      if (value === undefined) {
        finalScoped = zipRight(this.ensureMapSizeNotExceeded(newKey), scoped);
      } else {
        switch (value._tag) {
          case "Complete":
            {
              if (this.hasExpired(value.timeToLive)) {
                finalScoped = succeed(this.get(key));
              } else {
                const current = getOrUndefined(get(this.cacheState.map, key));
                if (equals(current, value)) {
                  const mapValue = refreshing(scoped, value);
                  set(this.cacheState.map, key, mapValue);
                  finalScoped = scoped;
                } else {
                  finalScoped = succeed(this.get(key));
                }
              }
              break;
            }
          case "Pending":
            {
              finalScoped = value.scoped;
              break;
            }
          case "Refreshing":
            {
              finalScoped = value.scoped;
              break;
            }
        }
      }
      return flatMap(finalScoped, s => scopedEffect(asUnit(s)));
    }));
  }
  size() {
    return sync(() => size(this.cacheState.map));
  }
  resolveMapValue(value, ignorePending = false) {
    switch (value._tag) {
      case "Complete":
        {
          this.trackHit();
          if (this.hasExpired(value.timeToLive)) {
            return succeed(succeedNone);
          }
          return as(this.ensureMapSizeNotExceeded(value.key), asSome(toScoped(value)));
        }
      case "Pending":
        {
          this.trackHit();
          if (ignorePending) {
            return succeed(succeedNone);
          }
          return zipRight(this.ensureMapSizeNotExceeded(value.key), map(value.scoped, asSome));
        }
      case "Refreshing":
        {
          this.trackHit();
          if (this.hasExpired(value.complete.timeToLive)) {
            if (ignorePending) {
              return succeed(succeedNone);
            }
            return zipRight(this.ensureMapSizeNotExceeded(value.complete.key), map(value.scoped, asSome));
          }
          return as(this.ensureMapSizeNotExceeded(value.complete.key), asSome(toScoped(value.complete)));
        }
    }
  }
  lookupValueOf(key) {
    return pipe(onInterrupt(flatMap(make$2(), scope => pipe(this.scopedLookup(key), provideContext(pipe(this.context, add(Scope, scope))), exit, map(exit => [exit, exit => close(scope, exit)]))), () => sync(() => remove(this.cacheState.map, key))), flatMap(([exit, release]) => {
      const now = this.clock.unsafeCurrentTimeMillis();
      const expiredAt = now + toMillis(this.timeToLive(exit));
      switch (exit._tag) {
        case "Success":
          {
            const exitWithFinalizer = succeed$1([exit.value, release]);
            const completedResult = complete(makeMapKey(key), exitWithFinalizer, make$1(1), makeEntryStats(now), expiredAt);
            let previousValue = undefined;
            if (has(this.cacheState.map, key)) {
              previousValue = getOrUndefined(get(this.cacheState.map, key));
            }
            set(this.cacheState.map, key, completedResult);
            return sync(() => flatten(as(this.cleanMapValue(previousValue), toScoped(completedResult))));
          }
        case "Failure":
          {
            const completedResult = complete(makeMapKey(key), exit, make$1(0), makeEntryStats(now), expiredAt);
            let previousValue = undefined;
            if (has(this.cacheState.map, key)) {
              previousValue = getOrUndefined(get(this.cacheState.map, key));
            }
            set(this.cacheState.map, key, completedResult);
            return zipRight(release(exit), sync(() => flatten(as(this.cleanMapValue(previousValue), toScoped(completedResult)))));
          }
      }
    }), memoize, flatten);
  }
  hasExpired(timeToLive) {
    return this.clock.unsafeCurrentTimeMillis() > timeToLive;
  }
  trackHit() {
    this.cacheState.hits = this.cacheState.hits + 1;
  }
  trackMiss() {
    this.cacheState.misses = this.cacheState.misses + 1;
  }
  trackAccess(key) {
    const cleanedKeys = [];
    offer(this.cacheState.accesses, key);
    if (compareAndSet(this.cacheState.updating, false, true)) {
      let loop = true;
      while (loop) {
        const key = poll(this.cacheState.accesses, EmptyMutableQueue);
        if (key === EmptyMutableQueue) {
          loop = false;
        } else {
          this.cacheState.keys.add(key);
        }
      }
      let size$1 = size(this.cacheState.map);
      loop = size$1 > this.capacity;
      while (loop) {
        const key = this.cacheState.keys.remove();
        if (key === undefined) {
          loop = false;
        } else {
          if (has(this.cacheState.map, key.current)) {
            const removed = getOrUndefined(get(this.cacheState.map, key.current));
            remove(this.cacheState.map, key.current);
            size$1 = size$1 - 1;
            cleanedKeys.push(removed);
            loop = size$1 > this.capacity;
          }
        }
      }
      set$1(this.cacheState.updating, false);
    }
    return cleanedKeys;
  }
  cleanMapValue(mapValue) {
    if (mapValue === undefined) {
      return unit;
    }
    switch (mapValue._tag) {
      case "Complete":
        {
          return releaseOwner(mapValue);
        }
      case "Pending":
        {
          return unit;
        }
      case "Refreshing":
        {
          return releaseOwner(mapValue.complete);
        }
    }
  }
  ensureMapSizeNotExceeded(key) {
    return forEachParUnboundedDiscard(this.trackAccess(key), cleanedMapValue => this.cleanMapValue(cleanedMapValue), false);
  }
}

/** @internal */
const make = options => {
  const timeToLive = decode(options.timeToLive);
  return makeWith({
    capacity: options.capacity,
    lookup: options.lookup,
    timeToLive: () => timeToLive
  });
};

/** @internal */
const makeWith = options => flatMap(clock, clock => buildWith(options.capacity, options.lookup, clock, exit => decode(options.timeToLive(exit))));
const buildWith = (capacity, scopedLookup, clock, timeToLive) => acquireRelease(flatMap(context(), context => sync(() => new ScopedCacheImpl(capacity, scopedLookup, clock, timeToLive, context))), cache => cache.invalidateAll());

export { ScopedCacheTypeId, complete, initialCacheState, make, makeCacheState, makeWith, pending, refreshing, releaseOwner, toScoped };
